import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Rocket, TrendingUp, Shield, Zap } from "lucide-react";
import { Link } from "react-router-dom";
import heroBg from "@/assets/hero-bg.jpg";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

export function HeroSection() {
  const [heroImageLoaded, setHeroImageLoaded] = useState(false);

  // Fetch real stats from database
  const { data: stats } = useQuery({
    queryKey: ["platform-stats"],
    queryFn: async () => {
      const [coinsResult, tradesResult] = await Promise.all([
        supabase.from("coins").select("id", { count: "exact" }),
        supabase.from("trades").select("amount_sol"),
      ]);
      
      const totalCoins = coinsResult.count ?? 0;
      const totalVolume = tradesResult.data?.reduce((sum, t) => sum + (t.amount_sol ?? 0), 0) ?? 0;
      const volumeInUsd = totalVolume * 200; // ~$200/SOL estimate
      
      return {
        coinsLaunched: totalCoins,
        tradingVolume: volumeInUsd,
      };
    },
    staleTime: 60000, // Cache for 1 minute
  });

  const formatVolume = (vol: number) => {
    if (vol >= 1000000) return `$${(vol / 1000000).toFixed(1)}M`;
    if (vol >= 1000) return `$${(vol / 1000).toFixed(1)}K`;
    return `$${vol.toFixed(0)}`;
  };

  return (
    <section className="relative overflow-hidden py-16 lg:py-24">
      {/* Background Image */}
      <div className="absolute inset-0 opacity-20 relative">
        {!heroImageLoaded && <Skeleton className="absolute inset-0" />}
        <img
          src={heroBg}
          alt=""
          className={cn("w-full h-full object-cover", !heroImageLoaded && "opacity-0")}
          onLoad={() => setHeroImageLoaded(true)}
          onError={() => setHeroImageLoaded(true)}
        />
      </div>
      <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-background/80 to-background" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-radial from-primary/10 via-transparent to-transparent blur-3xl" />
      
      <div className="container relative">
        <div className="max-w-4xl mx-auto text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-6 animate-fade-in">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
            </span>
            <span className="text-sm font-medium text-primary">
              Live on Solana Devnet
            </span>
          </div>

          {/* Main Title */}
          <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl font-black tracking-tight mb-6 animate-fade-in" style={{ animationDelay: '100ms' }}>
            <span className="text-foreground">Launch to the </span>
            <span className="gradient-text text-glow">Moon</span>
          </h1>

          {/* Subtitle */}
          <p className="text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto mb-8 animate-fade-in" style={{ animationDelay: '200ms' }}>
            The ultimate Solana meme coin launchpad. Fair launch with bonding curves, 
            no presales, and instant trading. Your community, your rules.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-wrap justify-center gap-4 mb-16 animate-fade-in" style={{ animationDelay: '300ms' }}>
            <Button variant="hero" size="xl" asChild>
              <Link to="/create">
                <Rocket className="h-5 w-5" />
                Launch Your Coin
              </Link>
            </Button>
            <Button variant="outline" size="xl" asChild>
              <Link to="/trending">
                Explore Coins
              </Link>
            </Button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-8 animate-fade-in" style={{ animationDelay: '400ms' }}>
            <StatCard 
              icon={Rocket} 
              value={stats?.coinsLaunched ? `${stats.coinsLaunched}+` : "0"} 
              label="Coins Launched" 
            />
            <StatCard 
              icon={TrendingUp} 
              value={stats?.tradingVolume ? formatVolume(stats.tradingVolume) : "$0"} 
              label="Trading Volume" 
            />
            <StatCard 
              icon={Shield} 
              value="100%" 
              label="Fair Launch" 
            />
            <StatCard 
              icon={Zap} 
              value="<1s" 
              label="Settlement" 
            />
          </div>
        </div>
      </div>
    </section>
  );
}

function StatCard({ 
  icon: Icon, 
  value, 
  label 
}: { 
  icon: React.ElementType; 
  value: string; 
  label: string;
}) {
  return (
    <div className="p-4 rounded-xl bg-card/50 border border-border/50 group hover:border-primary/30 transition-colors">
      <Icon className="h-5 w-5 text-primary mb-2 mx-auto group-hover:scale-110 transition-transform" />
      <p className="font-display text-2xl sm:text-3xl font-bold text-foreground mb-1">
        {value}
      </p>
      <p className="text-xs sm:text-sm text-muted-foreground">{label}</p>
    </div>
  );
}